import type React from 'react';

/**
 * Utilitário de extração de cor primária dominante do logotipo da empresa.
 * Executa em background via Canvas nativo sem bloquear a thread principal
 * e sem dependências externas pesadas.
 */

export interface ExtractedColorPalette {
  primary: string;       // ex: "#d97706" (Hex vibrante principal)
  primaryDark: string;   // ex: "#b45309" (Hex escuro para hover)
  primaryLight: string;  // ex: "#fef3c7" (Hex claro para fundos/badges)
}

const DEFAULT_BRAND_PALETTE: ExtractedColorPalette = {
  primary: '#059669',
  primaryDark: '#047857',
  primaryLight: '#ecfdf5'
};

/**
 * Converte RGB em Hexadecimal (#RRGGBB)
 */
function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    const clamped = Math.max(0, Math.min(255, Math.round(n)));
    return clamped.toString(16).padStart(2, '0');
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Ajusta o brilho de uma cor hex (positivo para clarear, negativo para escurecer)
 */
export function adjustBrightness(hex: string, percent: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const num = parseInt(clean, 16);
  let r = (num >> 16) + percent;
  let g = ((num >> 8) & 0x00ff) + percent;
  let b = (num & 0x0000ff) + percent;

  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));

  return rgbToHex(r, g, b);
}

/**
 * Analisa a imagem e extrai a cor primária mais representativa e vibrante.
 */
export async function extractDominantColor(imageSrc: string): Promise<ExtractedColorPalette> {
  if (!imageSrc || typeof window === 'undefined') {
    return DEFAULT_BRAND_PALETTE;
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    // Timeout de segurança para nunca travar a aplicação caso a URL falhe
    const timeout = setTimeout(() => {
      resolve(DEFAULT_BRAND_PALETTE);
    }, 2000);

    img.onload = () => {
      clearTimeout(timeout);
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          resolve(DEFAULT_BRAND_PALETTE);
          return;
        }

        // Reduz a imagem para 32x32 para processamento instantâneo
        const sampleSize = 32;
        canvas.width = sampleSize;
        canvas.height = sampleSize;
        ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

        const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize).data;
        const colorCounts: Record<string, { r: number; g: number; b: number; count: number; score: number }> = {};

        for (let i = 0; i < imageData.length; i += 4) {
          const r = imageData[i];
          const g = imageData[i + 1];
          const b = imageData[i + 2];
          const a = imageData[i + 3];

          // Ignora pixels transparentes
          if (a < 128) continue;

          // Ignora brancos puros / fundos claros de logos
          if (r > 235 && g > 235 && b > 235) continue;

          // Ignora pretos puros / cinzas muito escuros
          if (r < 25 && g < 25 && b < 25) continue;

          // Calcula saturação e vivacidade da cor
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const delta = max - min;
          const saturation = max === 0 ? 0 : delta / max;

          // Ignora tons muito acinzentados (saturação < 0.15)
          if (saturation < 0.15) continue;

          // Quantização em baldes de 16 para agrupar tons similares
          const qr = Math.round(r / 16) * 16;
          const qg = Math.round(g / 16) * 16;
          const qb = Math.round(b / 16) * 16;
          const key = `${qr},${qg},${qb}`;

          if (!colorCounts[key]) {
            colorCounts[key] = { r: qr, g: qg, b: qb, count: 0, score: 0 };
          }

          colorCounts[key].count += 1;
          // Pontua mais alto cores com saturação rica e bom contraste
          colorCounts[key].score += 1 + saturation * 2;
        }

        let bestKey: string | null = null;
        let maxScore = 0;

        for (const [key, val] of Object.entries(colorCounts)) {
          if (val.score > maxScore) {
            maxScore = val.score;
            bestKey = key;
          }
        }

        if (!bestKey || !colorCounts[bestKey]) {
          resolve(DEFAULT_BRAND_PALETTE);
          return;
        }

        const best = colorCounts[bestKey];
        const primary = rgbToHex(best.r, best.g, best.b);
        const primaryDark = adjustBrightness(primary, -30);
        const primaryLight = adjustBrightness(primary, 160);

        resolve({
          primary,
          primaryDark,
          primaryLight
        });
      } catch {
        resolve(DEFAULT_BRAND_PALETTE);
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      resolve(DEFAULT_BRAND_PALETTE);
    };

    img.src = imageSrc;
  });
}

/**
 * Converte Hex (#RRGGBB ou #RGB) em rgba(r, g, b, alpha)
 */
export function hexToRgba(hex: string, alpha: number): string {
  let clean = hex.replace('#', '').trim();
  if (clean.length === 3) {
    clean = `${clean[0]}${clean[0]}${clean[1]}${clean[1]}${clean[2]}${clean[2]}`;
  }
  if (clean.length !== 6) return `rgba(5, 150, 105, ${alpha})`;
  const num = parseInt(clean, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Gera conjunto completo de variáveis CSS customizadas para tematizar a vitrine
 * de agendamento online diretamente com a cor da logo do salão/empresa.
 */
export function generateStorefrontTheme(hexColor?: string): React.CSSProperties {
  const isHex = Boolean(hexColor && typeof hexColor === 'string' && /^#([0-9A-F]{3}){1,2}$/i.test(hexColor.trim()));
  const accent = isHex && hexColor ? hexColor.trim() : '#059669';

  const dark = adjustBrightness(accent, -25);
  const darker = adjustBrightness(accent, -45);

  return {
    '--color-brand': accent,
    '--color-brand-dark': dark,
    '--color-emerald-50': hexToRgba(accent, 0.08),
    '--color-emerald-100': hexToRgba(accent, 0.18),
    '--color-emerald-200': hexToRgba(accent, 0.32),
    '--color-emerald-300': hexToRgba(accent, 0.5),
    '--color-emerald-400': accent,
    '--color-emerald-500': accent,
    '--color-emerald-600': dark,
    '--color-emerald-700': dark,
    '--color-emerald-800': darker,
    '--color-emerald-900': darker,
    '--color-emerald-950': darker,
  } as React.CSSProperties;
}
